// ── UCC Register ─────────────────────────────────────────────────────────────
//
// What perfects the lender's security interests, and what is about to stop doing so.
//
// The closing checklist already asks "was the UCC filed?" once, at closing, and
// answers it with a tick and a document. This page asks the questions a tick
// cannot hold: which statement, in which office, against which name — and how
// long it has left.
//
// THE WORKLIST IS THE DEFAULT VIEW. A register nobody opens is a register that
// does not prevent anything, and a financing statement dies quietly: on lapse
// the security interest becomes unperfected and is deemed never to have been
// perfected against a purchaser for value (9-515(c)). No error, no notice from
// the filing office, and the closing checklist still says complete. So the
// first thing this page shows is the short list of filings that need somebody,
// and "All filings" is one click away rather than the other way round.
//
// NOTHING ON THIS PAGE FILES ANYTHING. Every button records what already
// happened at a Secretary of State. Ordering a filing or a search through CSC is
// a separate, deliberate action — the register is the book of record, not the
// filing agent.
//
// PULL FROM CSC reads the CSC order history (every filing goes through
// CSC, counsel's included) and brings it here. Continuations, amendments and
// terminations that point at a filing already in the register attach on their
// own; anything that needs a judgement — which borrower a new filing belongs to
// — waits in "From CSC — needs review" for a person to confirm. Pulling reads;
// it never orders anything.
import { useState, useEffect, useRef } from "react";
import { B } from "../../../shared/theme";
import { fmtDate, localFilter, exportCSV } from "../../../shared/format";
import { Badge, Btn, Card, ErrorMsg, FInput, Field, Modal, SearchBar, SectionHeader,
         Spinner, STh, Td, Th } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { LastRunTile, runOf, useMonitorFreshness } from "../components/LastRunTile";
import { US_STATE_NAMES } from "../../../shared/usStates";
import { openCscDoc, CscDocLinks, FilingDocs } from "../components/CscDocLinks";

// Recording a filing made OUTSIDE CSC (another firm filed it directly), and
// recording later events on such a filing. Nothing entered here reaches CSC.
// Events can only be recorded on hand-entered filings: a CSC filing's
// continuations and terminations arrive on the pull, and a hand-recorded one
// would double up when CSC's arrives.
const ALLOW_RECORD_FILING = true;
const isOutsideCSC = v => v.source !== "csc";

const STATE_CODES = Object.keys(US_STATE_NAMES)
  .sort((a, b) => US_STATE_NAMES[a].localeCompare(US_STATE_NAMES[b]));

const EVENT_TYPES = ["Continuation", "Amendment", "Assignment", "Termination", "Correction"];

// The Status badge colours the STATUS — the filing's own life cycle. What is
// wrong around it (wrong office, renamed debtor) is said in words in the Needs
// column, so an Active filing reads green and the problem reads as a problem,
// instead of an "Active" in red that leaves you to guess why.
const statusStyle = v => {
  if (v.status === "LAPSED")      return { bg:"#FBE4DD", fg:"#993C1D" };
  if (v.in_continuation_window)   return { bg:"#FDF0D5", fg:"#7A5200" };
  if (v.status === "Terminated")  return { bg:"#F1EFE8", fg:"#888780" };
  if (v.status === "No lapse")    return { bg:"#F1EFE8", fg:"#888780" };
  return { bg:"#E1F5EE", fg:"#0F6E56" };
};

// Everything a filing needs, each in its own words, most urgent first. Built
// from the flags rather than the worklist's single "issue" line, so a filing
// with two problems shows both, and the "All filings" view (which has no issue
// line) says the same as the worklist. Red = it may not perfect; amber = act
// before a deadline.
const needsOf = v => {
  if (v.status === "Terminated") return [];
  const out = [];
  if (v.status === "LAPSED")
    out.push({ red:true, text:"Lapsed — the security interest is unperfected" });
  if (v.jurisdiction_mismatch)
    out.push({ red:true, text:`State mismatch: filed in ${v.jurisdiction_state}, but ACE has `
      + `${v.debtorname_now || "the debtor entity"} registered in ${v.debtorstate_now}. `
      + `Either the filing is in the wrong office, or the entity (or its state) in ACE is wrong.` });
  if (v.name_drift)
    out.push({ red:false, text:`Name mismatch: filed as "${v.debtorname_asfiled}", ACE entity is "${v.debtorname_now}". `
      + `A rename needs an amendment within four months; a different entity means the wrong one is linked.` });
  if (v.in_continuation_window)
    out.push({ red:false, text:`Continuation window open — ${v.days_to_lapse} days to lapse` });
  return out;
};

// Portfolio status order for the register's grouping: live books first,
// paid off last. Anything not listed (a status added later) sits just above
// Paid Off rather than disappearing.
const STATUS_ORDER = ["Accrual", "Non Accrual", "Funding in Progress", "Charged Off", "Terminated"];
const statusRank = st => {
  if (st === "Paid Off") return 99;
  const i = STATUS_ORDER.indexOf(st);
  return i === -1 ? 50 : i;
};

const Tile = ({ label, value, tone, onClick, active }) => (
  <div onClick={onClick}
       style={{ background: active ? (tone||"#E1F5EE") : "var(--color-background-secondary)",
                border: "0.5px solid " + (active ? "transparent" : "var(--color-border-tertiary)"),
                borderRadius:8, padding:"10px 14px", cursor:onClick?"pointer":"default", minWidth:120 }}>
    <div style={{ fontSize:11, color:"var(--color-text-secondary)", textTransform:"uppercase",
                  letterSpacing:"0.05em", marginBottom:3 }}>{label}</div>
    <div style={{ fontSize:20, fontWeight:600, fontVariantNumeric:"tabular-nums" }}>{value}</div>
  </div>
);

export const UCCRegisterPage = () => {
  const { api, role, go, view } = useApp();
  const fresh = useMonitorFreshness();   // "Last CSC pull" tile
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  // worklist | all | an issue tile | nofiling (opened from the dashboard's Coverage line)
  const [mode, setMode] = useState(() => (view && view.mode === "nofiling" ? "nofiling" : "worklist"));
  const [accts, setAccts] = useState([]);         // for "No filing": live borrowers
  const [acks, setAcks] = useState([]);           // reasons on record (UCCSearchFindingAck)
  const [detail, setDetail] = useState(null);
  const [adding, setAdding] = useState(false);
  const [note, setNote] = useState(null);
  const [csc, setCsc] = useState(null);           // getCSCOrders summary (null = not set up)
  const [pulling, setPulling] = useState(false);

  const load = () => {
    setLoading(true); setErr(null);
    // Always the full set: the worklist is a filter over it, so switching views
    // is instant and the tiles can count both without a second round trip.
    api.get("getUCCFilings", { events: 1 })
      .then(d => {
        setRows(Array.isArray(d && d.rows) ? d.rows : []);
        setEvents(Array.isArray(d && d.events) ? d.events : []);
        setLoading(false);
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  // The CSC queue loads on its own so a missing table (SQL not yet run) or a
  // CSC outage never blanks the register itself.
  const loadCsc = () => api.get("getCSCOrders", { view: "review" })
    .then(d => setCsc(d || null)).catch(() => setCsc(null));
  const loadAcks = () => api.get("getUCCSearches", { acksOnly: 1 })
    .then(d => setAcks((d && d.acks) || [])).catch(() => setAcks([]));
  useEffect(() => { load(); loadCsc(); loadAcks();
    api.get("getAccounts").then(d => setAccts(Array.isArray(d) ? d : [])).catch(() => {}); }, []);

  // Funded borrowers only (Accrual, Non Accrual): a paid-off borrower's filing
  // lapsing is expected, not work. The same rule as the sidebar badge and the
  // Action Center, so the three numbers are one number. Other statuses' filings
  // are still listed under Filings.
  const isLiveAcct = v => ["Accrual", "Non Accrual"].includes(v.accountstatus);
  const needsAttention = v => v.status !== "Terminated" && isLiveAcct(v) &&
    (v.status === "LAPSED" || v.jurisdiction_mismatch || v.name_drift || v.in_continuation_window);

  // The issue tiles count live filings only — the same rule as Needs attention
  // and the worklist. A terminated filing's old name or office is history, not
  // work, so it no longer shows "Name drift 2" above an empty list.
  // Each tile is also a filter: click it to list exactly those filings.
  const live = rows.filter(v => v.status !== "Terminated" && isLiveAcct(v));
  const ISSUE = {
    lapsed:   v => v.status === "LAPSED",
    window:   v => !!v.in_continuation_window,
    drift:    v => !!v.name_drift,
    mismatch: v => !!v.jurisdiction_mismatch,
  };
  const counts = {
    all:        rows.length,
    lapsed:     live.filter(ISSUE.lapsed).length,
    window:     live.filter(ISSUE.window).length,
    drift:      live.filter(ISSUE.drift).length,
    mismatch:   live.filter(ISSUE.mismatch).length,
    nofiling:   0, // set below
    attention:  rows.filter(needsAttention).length,
  };

  // ── No filing: funded borrowers (Accrual, Non Accrual) with no live, unlapsed
  // filing in the register — the dashboard's Coverage line. A borrower with a
  // reason on record (marked here, or a search finding marked known on UCC
  // Searches) is listed separately and not counted.
  const coveredIds = new Set(rows.filter(v => v.status !== "Terminated" && v.status !== "LAPSED").map(v => String(v.accountid)));
  const reasonsBy = new Map();
  for (const a of acks) { const k = String(a.accountid); if (!reasonsBy.has(k)) reasonsBy.set(k, []); reasonsBy.get(k).push(a); }
  const noFiling = accts.filter(a => ["Accrual", "Non Accrual"].includes(a.accountstatus) && !coveredIds.has(String(a.id)))
    .sort((a, b) => String(a.companyname).localeCompare(String(b.companyname)));
  const noFilingOpen = noFiling.filter(a => !reasonsBy.has(String(a.id)));
  const noFilingExcused = noFiling.filter(a => reasonsBy.has(String(a.id)));
  counts.nofiling = noFilingOpen.length;

  const base = mode === "worklist" ? rows.filter(needsAttention)
             : ISSUE[mode] ? live.filter(ISSUE[mode]) : rows;
  const filtered = localFilter(base, q,
    ["companyname","debtorname_asfiled","filingnumber","jurisdiction_state","jurisdiction_office","status"]);
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, "days_to_lapse");
  // Grouped: portfolio status (Accrual first, Paid Off last), then borrower by
  // name, then that borrower's filings in whatever order the columns are sorted.
  const [closed, setClosed] = useState({});
  const groups = (() => {
    const byStatus = new Map();
    for (const v of sorted) {
      const st = v.accountstatus || "Unknown";
      if (!byStatus.has(st)) byStatus.set(st, new Map());
      const bk = String(v.accountid || v.companyname || "—");
      const m = byStatus.get(st);
      if (!m.has(bk)) m.set(bk, { name: v.companyname || "—", rows: [] });
      m.get(bk).rows.push(v);
    }
    return [...byStatus.entries()]
      .sort((a, b) => statusRank(a[0]) - statusRank(b[0]) || a[0].localeCompare(b[0]))
      .map(([status, m]) => ({ status,
        borrowers: [...m.values()].sort((a, b) => a.name.localeCompare(b.name)) }));
  })();

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const eventsFor = id => events.filter(e => e.uccfilingid === id);

  return <>
    <SectionHeader actions={[
      <SearchBar key="s" value={q} onChange={setQ} placeholder="Search borrower, debtor, filing number..." />,
      // Worklist / all filings is switched with the Needs attention and Filings cards.
      <Btn key="ex" onClick={()=>exportCSV(sorted,
        ["accountstatus","companyname","debtorname_asfiled","filingnumber","jurisdiction_state","jurisdiction_office",
         "filedate","lapsedate","days_to_lapse","status","continuations","source"],"ucc_register.csv")}>Export CSV</Btn>,
      // A filing made away from CSC is added from the borrower's own page
      // (Account → Monitoring → UCC Filings), where the borrower is already known.
      false && ALLOW_RECORD_FILING && role.canEdit && <Btn key="new" onClick={()=>setAdding(true)}>＋ Add filing made outside CSC</Btn>,
    ].filter(Boolean)} />

    {note && (
      <div style={{ fontSize:12, background:"var(--color-background-secondary)",
                    border:"0.5px solid var(--color-border-tertiary)", borderRadius:8,
                    padding:"8px 12px", marginBottom:12 }}>{note}</div>
    )}

    <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
      <Tile label="Needs attention" value={counts.attention} tone="#FDF0D5"
            onClick={()=>setMode("worklist")} active={mode==="worklist" && counts.attention > 0} />
      <Tile label="Filings" value={counts.all} onClick={()=>setMode("all")} active={mode==="all"} />
      {[["lapsed","Lapsed","#FBE4DD"],["window","Continuation open","#FDF0D5"],
        ["drift","Name drift","#FDF0D5"],["mismatch","Wrong office","#FBE4DD"]].map(([k, label, tone]) =>
        <Tile key={k} label={label} value={counts[k]} tone={tone}
              active={mode === k || (mode !== k && counts[k] > 0 && !ISSUE[mode])}
              onClick={() => setMode(m => m === k ? "worklist" : k)} />)}
      <Tile label="No filing" value={counts.nofiling} tone="#FBE4DD"
            active={mode === "nofiling" || (counts.nofiling > 0 && !ISSUE[mode])}
            onClick={() => setMode(m => m === "nofiling" ? "worklist" : "nofiling")} />
      {/* Is the morning CSC pull still running? Red after 48 hours or a failed run. */}
      <LastRunTile label="Last CSC pull" run={runOf.csc(fresh)} staleHours={48} />
      {/* Pull from CSC sits at the end of the card row, in the primary blue. */}
      {role.isAdmin && <div style={{ marginLeft:"auto", alignSelf:"center" }}>
        <Btn variant="primary" onClick={()=>setPulling(true)}>⇣ Pull from CSC</Btn>
      </div>}
    </div>

    {rows.length === 0 && (
      <div style={{ fontSize:13, color:"#7a5200", background:"#FAEEDA", border:"0.5px solid #EBD3A0",
                    borderRadius:8, padding:"12px 16px", marginBottom:16 }}>
        <strong>The register is empty.</strong> If your filings — counsel's included — are ordered
        through CSC, start with <em>Pull from CSC</em>: it reads the order history and brings each filing
        here for you to confirm the borrower. Order new filings on CSC's website; they arrive here on the next pull.
      </div>
    )}

    <CSCQueue summary={csc} filings={rows} canEdit={role.canEdit}
              onChanged={m=>{ if (m) setNote(m); load(); loadCsc(); }} />

    {mode === "nofiling" && <NoFilingCard open={noFilingOpen} excused={noFilingExcused} reasonsBy={reasonsBy}
                                          canEdit={role.canEdit} go={go} api={api} onChanged={loadAcks} />}

    {mode !== "nofiling" && <Card style={{ overflowX:"auto" }}>
      <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
        <thead><tr>
          <STh label="Debtor as filed" sortKey="debtorname_asfiled" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Filing #"    sortKey="filingnumber"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="CSC Order #" sortKey="csc_orderid"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Office"      sortKey="jurisdiction_state" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Filed"       sortKey="filedate"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Lapses"      sortKey="lapsedate"          currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Days"        sortKey="days_to_lapse"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <Th>Status</Th>
          <Th>Documents</Th>
          <Th>Needs</Th>
        </tr></thead>
        <tbody>{groups.map(g => {
          const filings = g.borrowers.reduce((n, b) => n + b.rows.length, 0);
          const isClosed = !!closed[g.status];
          return [
            // Same band as the Accounts and Loans tables: light theme blue, the
            // status badge, and the counts beside it.
            <tr key={"s-" + g.status} onClick={()=>setClosed(c => ({ ...c, [g.status]: !c[g.status] }))}
                style={{ cursor:"pointer" }}>
              <td colSpan={10} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase",
                                       letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
                <span style={{ marginRight:6 }}>{isClosed ? "▸" : "▾"}</span>
                <Badge s={g.status} />
                <span style={{ marginLeft:8, color:B.cyanDark, fontSize:11 }}>
                  {g.borrowers.length} borrower{g.borrowers.length === 1 ? "" : "s"} · {filings} filing{filings === 1 ? "" : "s"}
                </span>
              </td>
            </tr>,
            ...(isClosed ? [] : g.borrowers.flatMap(b => [
              // Borrower ribbon: the status band's blue at under half strength,
              // so the company stands out from its filings without competing
              // with the status band above it.
              <tr key={"b-" + g.status + b.name}>
                <td colSpan={10} style={{ padding:"7px 10px 7px 22px", fontSize:13, fontWeight:600, color:B.cyanDark,
                                          background:`color-mix(in srgb, ${B.cyanLight} 40%, transparent)`,
                                          borderBottom:"0.5px solid var(--color-border-tertiary)" }}>
                  {b.name}
                  <span style={{ fontWeight:400, color:"var(--color-text-secondary)", marginLeft:8, fontSize:12 }}>
                    {b.rows.length} filing{b.rows.length === 1 ? "" : "s"}
                  </span>
                </td>
              </tr>,
              ...b.rows.map(v => {
          const s = statusStyle(v);
          return (
            <tr key={v.uccfilingid} style={{ cursor:"pointer" }}
                onClick={()=>setDetail(v)}
                onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"}
                onMouseLeave={e=>e.currentTarget.style.background=""}>
              {/* The name ON the filing, not the name we use today. When they
                  differ the row is flagged — that difference is a four-month
                  clock under 9-507(c), and nothing else would notice it. */}
              <Td style={{ paddingLeft:34 }}>{v.debtorname_asfiled || "—"}</Td>
              <Td><span style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{v.filingnumber || "—"}</span></Td>
              <Td><span style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{v.csc_orderid ? String(v.csc_orderid).replace(/-\d+$/, "") : "—"}</span></Td>
              <Td>{v.jurisdiction_state || "—"}{v.jurisdiction_office ? " · " + v.jurisdiction_office : ""}</Td>
              <Td>{fmtDate(v.filedate)}</Td>
              <Td>{v.lapsedate ? fmtDate(v.lapsedate) : <span style={{ color:"var(--color-text-secondary)" }}>never</span>}</Td>
              <Td style={{ textAlign:"right", fontVariantNumeric:"tabular-nums",
                           color: v.days_to_lapse != null && v.days_to_lapse < 180 ? "#993C1D" : "inherit" }}>
                {v.days_to_lapse == null ? "—" : v.days_to_lapse}
              </Td>
              <Td><span style={{ fontSize:11.5, background:s.bg, color:s.fg, padding:"2px 8px",
                                 borderRadius:4, fontWeight:600 }}>{v.status}</span></Td>
              <Td><FilingDocs api={api} v={v} compact /></Td>
              <Td style={{ fontSize:12, whiteSpace:"normal", maxWidth:360 }}>
                {needsOf(v).map((n, i) => <div key={i} style={{ color: n.red ? "#993C1D" : "#7a5200",
                                                                marginBottom: 2 }}>{n.text}</div>)}
              </Td>
            </tr>
          );
        })]))];
        })}</tbody>
      </table>
      {sorted.length === 0 && rows.length > 0 && (
        <div style={{ padding:20, textAlign:"center", fontSize:13, color:"var(--color-text-secondary)" }}>
          {mode==="worklist"
            ? "Nothing needs attention — every filing is current, in the right office, and against the right name."
            : "No filings match that search."}
        </div>
      )}
    </Card>}

    {detail && <FilingDetail v={detail} events={eventsFor(detail.uccfilingid)}
                             onClose={()=>setDetail(null)}
                             onSaved={m=>{ setDetail(null); setNote(m); load(); }} />}
    {pulling && <CSCPullModal summary={csc} onClose={()=>setPulling(false)}
                              onDone={m=>{ setPulling(false); if (m) setNote(m); load(); loadCsc(); }} />}
    {adding && <FilingForm onClose={()=>setAdding(false)}
                           onSaved={m=>{ setAdding(false); setNote(m); load(); }} />}
  </>;
};

// ── No filing: funded borrowers the register has no live filing for ──────────
// Two ways to clear one: file (order through CSC — it arrives on the next pull —
// or, for a filing another firm made, add it from the account's Monitoring tab),
// or record why none is needed (a participation, an unsecured facility …).
// The reason is kept with the borrower's other UCC notes and can be re-opened.
const NO_FILING_KEY = "nofiling:-";
const NoFilingCard = ({ open, excused, reasonsBy, canEdit, go, api, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [showExcused, setShowExcused] = useState(false);
  const muted = { color:"var(--color-text-secondary)" };
  const act = async body => { setBusy(true);
    try { await api.post("saveCSCOrderReview", body); } catch (e) { alert(e.message); }
    setBusy(false); onChanged(); };
  const excuse = a => {
    const note = window.prompt(`Why does ${a.companyname} not need a UCC filing? (e.g. participation — lead lender holds the lien; unsecured facility)`, "");
    if (!note) return;
    act({ action: "ackFinding", accountid: a.id, findingkey: NO_FILING_KEY, note });
  };
  const row = (a, isExcused) => {
    const rs = reasonsBy.get(String(a.id)) || [];
    const own = rs.find(r => r.findingkey === NO_FILING_KEY);
    return <tr key={a.id} style={{ opacity: isExcused ? 0.75 : 1 }}>
      <Td style={{ fontWeight:500 }}><a href="#" onClick={e => { e.preventDefault(); go("account", { accountid: a.id, companyname: a.companyname }); }}
                                       style={{ color:B.cyanDark }}>{a.companyname}</a></Td>
      <Td><Badge s={a.accountstatus} /></Td>
      <Td style={{ whiteSpace:"normal", fontSize:12.5 }}>{rs.length
        ? rs.map((r, i) => <div key={i}>{r.note || "—"} <span style={{ ...muted, fontSize:11.5 }}>
            ({r.findingkey === NO_FILING_KEY ? "no filing needed" : "UCC search finding marked known"} · {r.ackby})</span></div>)
        : <span style={muted}>—</span>}</Td>
      <Td><div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
        <Btn onClick={() => go("account", { accountid: a.id, companyname: a.companyname })}>Open account</Btn>
        {canEdit && !isExcused && <Btn disabled={busy} onClick={() => excuse(a)}>No filing needed…</Btn>}
        {canEdit && own && <Btn disabled={busy} onClick={() => act({ action: "clearAck", accountid: a.id, findingkey: NO_FILING_KEY })}>Re-open</Btn>}
      </div></Td>
    </tr>;
  };
  return <Card style={{ overflowX:"auto", marginBottom:16 }}>
    <div style={{ fontSize:12, ...muted, marginBottom:8 }}>
      Funded borrowers (Accrual, Non Accrual) with no live filing in the register. File through CSC — it arrives on the
      next Pull from CSC — or, for a filing another firm made, add it on the account's Monitoring tab. If none is
      needed, record why: the borrower then drops off this list and the dashboard's Coverage line.
    </div>
    <table style={{ width:"100%", borderCollapse:"collapse" }}>
      <thead><tr><Th>Borrower</Th><Th>Status</Th><Th>Reason on record</Th><Th></Th></tr></thead>
      <tbody>
        {open.map(a => row(a, false))}
        {!open.length && <tr><td colSpan={4} style={{ padding:14, fontSize:13, ...muted }}>Every funded borrower has a live filing or a reason on record.</td></tr>}
        {excused.length > 0 && <tr onClick={() => setShowExcused(x => !x)} style={{ cursor:"pointer" }}>
          <td colSpan={4} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase",
                                   letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
            {showExcused ? "▾" : "▸"} Reason on record ({excused.length})</td></tr>}
        {showExcused && excused.map(a => row(a, true))}
      </tbody>
    </table>
  </Card>;
};

// ── One filing: what it says, what happened to it, and what to record next ──
// Exported: the account's Monitoring tab opens the same detail for a filing.
export const FilingDetail = ({ v, events, onClose, onSaved }) => {
  const { api, role } = useApp();
  const [form, setForm] = useState(null);      // the event being recorded
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [fixing, setFixing] = useState(false);   // correcting borrower / filed date
  // The whole manual filing form — the one on an account's Monitoring tab —
  // for a filing made outside CSC: every field, office included.
  const [editAll, setEditAll] = useState(false);

  const blank = { eventtype:"Continuation", eventdate:"", filingnumber:"", newlapsedate:"", description:"" };
  const set = k => val => setForm(f => ({ ...f, [k]: val }));

  const save = async () => {
    setBusy(true); setErr(null); setWarnings([]);
    try {
      const res = await api.post("saveUCCFilingEvent", { uccfilingid: v.uccfilingid, ...form });
      // The warnings are the point of the call, not an afterthought: a
      // continuation filed outside the six-month window is ineffective, and the
      // user has to see that before they close the dialog believing it worked.
      if (res.warnings && res.warnings.length) { setWarnings(res.warnings); setBusy(false); return; }
      onSaved(form.eventtype + " recorded"
        + (res.after && res.after.lapsedate ? " — lapse now " + String(res.after.lapsedate).slice(0,10) : ""));
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const row = (k, val) => (
    <div style={{ display:"flex", gap:8, fontSize:13, padding:"3px 0" }}>
      <div style={{ width:150, color:"var(--color-text-secondary)", flexShrink:0 }}>{k}</div>
      <div style={{ fontWeight:500 }}>{val || "—"}</div>
    </div>
  );

  // Editing the whole filing: the same form as the account page, in place of
  // this window. Saving closes both and reloads; Cancel comes back here.
  if (editAll) return <FilingForm rec={v} onClose={()=>setEditAll(false)} onSaved={onSaved} />;

  return <Modal title={"UCC · " + (v.companyname || "")} onClose={onClose} wide>
    <div style={{ padding:20 }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24 }}>
        <div>
          {row("Debtor as filed", v.debtorname_asfiled)}
          {v.name_drift === true && (
            <div style={{ fontSize:12, color:"#7a5200", background:"#FAEEDA", border:"0.5px solid #EBD3A0",
                          borderRadius:8, padding:"8px 12px", margin:"6px 0" }}>
              The entity is now <strong>{v.debtorname_now}</strong>. A debtor name change makes a financing
              statement seriously misleading (9-507(c)) — collateral acquired more than four months after
              the change is not covered unless an amendment is filed.
            </div>
          )}
          {row("Filing number", v.filingnumber)}
          {row("Office", [v.jurisdiction_state, v.jurisdiction_office].filter(Boolean).join(" · "))}
          {v.jurisdiction_mismatch === true && (
            <div style={{ fontSize:12, color:"#993C1D", background:"#FBE4DD", border:"0.5px solid #E8B4A8",
                          borderRadius:8, padding:"8px 12px", margin:"6px 0" }}>
              Filed in <strong>{v.jurisdiction_state}</strong> but the entity is registered in{" "}
              <strong>{v.debtorstate_now}</strong>. Under 9-307(e) the filing office is the state of
              registration — a statement filed elsewhere does not perfect.
            </div>
          )}
          {row("Secured party", v.securedparty_asfiled)}
          {row("Source", v.source)}
        </div>
        <div>
          {row("Filed", fmtDate(v.filedate))}
          {row("Lapses", v.lapsedate ? fmtDate(v.lapsedate) : "never")}
          {row("Days to lapse", v.days_to_lapse == null ? "—" : v.days_to_lapse)}
          {row("Continuations", v.continuations)}
          {row("Status", v.status)}
          {v.cscdocs && v.cscdocs.length > 0 && <div style={{ marginTop:8, fontSize:13 }}>
            <CscDocLinks api={api} docs={v.cscdocs} />
          </div>}
          {v.box_url && <div style={{ marginTop:8 }}>
            <a href={v.box_url} target="_blank" rel="noopener noreferrer"
               style={{ color:B.cyanDark, fontWeight:500, fontSize:13 }}>View filed document</a>
          </div>}
        </div>
      </div>

      {v.collateraldescription && (
        <div style={{ marginTop:16 }}>
          <div style={{ fontSize:11, fontWeight:600, color:"var(--color-text-secondary)",
                        textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:4 }}>Collateral</div>
          <div style={{ fontSize:13, whiteSpace:"pre-wrap" }}>{v.collateraldescription}</div>
        </div>
      )}

      <div style={{ marginTop:20 }}>
        <div style={{ fontSize:11, fontWeight:600, color:"var(--color-text-secondary)",
                      textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>History</div>
        {events.length === 0
          ? <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>Nothing recorded yet.</div>
          : <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <tbody>{events.map(e => (
                <tr key={e.id}>
                  <Td style={{ width:120 }}>{fmtDate(e.eventdate)}</Td>
                  <Td style={{ width:130, fontWeight:500 }}>{e.eventtype}</Td>
                  <Td style={{ fontFamily:"var(--font-mono)", fontSize:12, width:160 }}>{e.filingnumber||"—"}</Td>
                  <Td style={{ whiteSpace:"normal" }}>{e.description||""}</Td>
                </tr>
              ))}</tbody>
            </table>}
      </div>

      <CSCFilingHistory uccfilingid={v.uccfilingid} />

      {role.canEdit && (
        <div style={{ marginTop:20, paddingTop:16, borderTop:"0.5px solid var(--color-border-tertiary)" }}>
          {fixing ? <CorrectFiling v={v} onCancel={()=>setFixing(false)} onSaved={onSaved} />
          : !form ? (
            <div style={{ display:"flex", gap:8 }}>
              {/* Only on filings entered by hand: a CSC filing's events come
                  from CSC on the pull. Recording here never reaches anyone. */}
              {ALLOW_RECORD_FILING && isOutsideCSC(v) && <Btn variant="primary" onClick={()=>{ setForm(blank); setWarnings([]); setErr(null); }}>
                Record an event
              </Btn>}
              {v.source !== "csc" && <Btn onClick={()=>setEditAll(true)}
                   title="Every field of this hand-entered filing — office, number, dates, debtor, secured party, collateral">Edit filing</Btn>}
              <Btn onClick={()=>setFixing(true)}>Correct borrower / filed date</Btn>
            </div>
          ) : <>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>Record an event</div>
            <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:10 }}>
              For this filing made outside CSC: records a continuation, amendment or termination the other firm
              filed. It does not file anything and nothing is sent to CSC.
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 16px" }}>
              <Field label="Type">
                <select value={form.eventtype} onChange={e=>set("eventtype")(e.target.value)}
                        style={{ width:"100%", padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #999" }}>
                  {EVENT_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Date filed">
                <input type="date" value={form.eventdate} onChange={e=>set("eventdate")(e.target.value)}
                       style={{ width:"100%", boxSizing:"border-box", padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #999" }} />
              </Field>
              <Field label="Document number"><FInput value={form.filingnumber} onChange={set("filingnumber")} /></Field>
              {/* Only for a continuation, and optional even then: the view
                  computes the new lapse from the chain. This is for when the
                  filing office states a date explicitly — a stated date always
                  beats a derived one. */}
              {form.eventtype === "Continuation" && (
                <Field label="New lapse date (if stated)">
                  <input type="date" value={form.newlapsedate} onChange={e=>set("newlapsedate")(e.target.value)}
                         style={{ width:"100%", boxSizing:"border-box", padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #999" }} />
                </Field>
              )}
              <div style={{ gridColumn:"1/-1" }}>
                <Field label="Note"><FInput value={form.description} onChange={set("description")} /></Field>
              </div>
            </div>

            {warnings.length > 0 && (
              <div style={{ fontSize:12.5, color:"#993C1D", background:"#FBE4DD", border:"0.5px solid #E8B4A8",
                            borderRadius:8, padding:"10px 14px", marginTop:8 }}>
                {warnings.map((w,i)=><div key={i} style={{ marginBottom:i<warnings.length-1?6:0 }}>{w}</div>)}
                <div style={{ marginTop:8, fontWeight:600 }}>
                  Nothing was saved. Press Record again to save it anyway — the register should say what the
                  filing office actually did, even when what it did has no legal effect.
                </div>
              </div>
            )}
            {err && <div style={{ color:"#993C1D", fontSize:12, marginTop:8 }}>{err}</div>}

            <div style={{ display:"flex", gap:8, marginTop:12 }}>
              <Btn variant="primary" disabled={busy || !form.eventdate} onClick={()=>{
                if (warnings.length) { /* second press: keep the event, drop the guard */ setWarnings([]); }
                save();
              }}>{busy ? "Saving…" : warnings.length ? "Record anyway" : "Record"}</Btn>
              <Btn disabled={busy} onClick={()=>{ setForm(null); setWarnings([]); setErr(null); }}>Cancel</Btn>
            </div>
          </>}
        </div>
      )}
    </div>
  </Modal>;
};

// ── What CSC knows about this filing ────────────────────────────────────────
// Read from the stored CSC orders (no call to CSC): who placed, created and
// submitted each filing, when the office approved and acknowledged it, the
// parties as filed with addresses, and CSC's dated history for the UCC-1 and
// every change attached to it. Renders nothing for a filing not from CSC.
const fmtWhen = t => {
  if (!t) return "—";
  // CSC's times carry no zone and are CSC's clock (Eastern); shown as given,
  // not shifted, so they match what CSC's own screens say.
  const d = new Date(String(t).replace(/([+-]\d\d:\d\d|Z)$/, ""));
  return isNaN(d) ? String(t) : d.toLocaleString("en-US", { month:"2-digit", day:"2-digit", year:"2-digit",
                                                           hour:"numeric", minute:"2-digit" });
};
const CSCFilingHistory = ({ uccfilingid }) => {
  const { api } = useApp();
  const [orders, setOrders] = useState(null);
  const [showInternal, setShowInternal] = useState(true);
  useEffect(() => {
    api.get("getCSCFilingHistory", { uccfilingid })
      .then(d => setOrders((d && d.orders) || [])).catch(() => setOrders([]));
  }, [uccfilingid]);
  if (!orders || !orders.length) return null;

  const label = { fontSize:11, fontWeight:600, color:"var(--color-text-secondary)", textTransform:"uppercase",
                  letterSpacing:"0.05em", marginBottom:6 };
  const row = (k, val) => val ? (
    <div style={{ display:"flex", gap:8, fontSize:12.5, padding:"2px 0" }}>
      <div style={{ width:130, color:"var(--color-text-secondary)", flexShrink:0 }}>{k}</div>
      <div style={{ fontWeight:500, minWidth:0 }}>{val}</div>
    </div>) : null;
  const evName = t => t === "Initial" ? "UCC-1" : t;
  const history = orders.flatMap(o => o.notes.map(n => ({ ...n, ev: evName(o.eventtype), order: o.csc_orderid })))
    .filter(n => showInternal || n.external)
    .sort((a, b) => String(a.at).localeCompare(String(b.at)));

  return <div style={{ marginTop:20 }}>
    <div style={label}>From CSC</div>
    {orders.map(o => (
      <div key={o.csc_orderid} style={{ border:"0.5px solid var(--color-border-tertiary)", borderRadius:8,
                                         padding:"10px 14px", marginBottom:10 }}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:6 }}>
          {o.filingtype || evName(o.eventtype)} · {o.filingnumber || "no filing #"}
          <span style={{ fontWeight:400, color:"var(--color-text-secondary)", marginLeft:8, fontSize:12 }}>
            CSC order {o.csc_orderid}{o.status ? " · " + o.status : ""}</span>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 24px" }}>
          <div>
            {row("Placed by", o.placedBy)}
            {row("Created by", o.createdBy && (o.createdBy + (o.createdAt ? " · " + fmtWhen(o.createdAt) : "")))}
            {row("Submitted by", o.submittedBy && (o.submittedBy + (o.submittedAt ? " · " + fmtWhen(o.submittedAt) : "")))}
            {row("Amendment", o.amendment)}
            {row("Collateral type", o.collateralType)}
          </div>
          <div>
            {row("Delivered to office", o.deliveredAt && fmtWhen(o.deliveredAt))}
            {row("Approved", o.approvedAt && fmtWhen(o.approvedAt))}
            {row("Acknowledged", o.acknowledgedAt && fmtWhen(o.acknowledgedAt))}
            {row("Expires", o.expirationDate && fmtDate(o.expirationDate))}
            {row("CSC filing ID", o.cscFilingId)}
            {row("CSC account", o.billingAccount)}
          </div>
        </div>
        {(o.debtors.length > 0 || o.securedParties.length > 0) &&
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 24px", marginTop:6 }}>
            <div>{o.debtors.map((p, i) => <div key={i} style={{ fontSize:12.5, padding:"2px 0" }}>
              <span style={{ color:"var(--color-text-secondary)" }}>Debtor: </span><strong>{p.name}</strong>
              {p.address && <div style={{ color:"var(--color-text-secondary)" }}>{p.address}</div>}
              {(p.orgJurisdiction || p.orgId || p.orgType) && <div style={{ color:"var(--color-text-secondary)" }}>
                {[p.orgType, p.orgJurisdiction && "organized in " + p.orgJurisdiction, p.orgId && "org ID " + p.orgId]
                  .filter(Boolean).join(" · ")}</div>}
            </div>)}</div>
            <div>{o.securedParties.map((p, i) => <div key={i} style={{ fontSize:12.5, padding:"2px 0" }}>
              <span style={{ color:"var(--color-text-secondary)" }}>Secured party{p.assignor ? " (assignor)" : ""}: </span>
              <strong>{p.name}</strong>
              {p.address && <div style={{ color:"var(--color-text-secondary)" }}>{p.address}</div>}
            </div>)}</div>
          </div>}
      </div>
    ))}

    {history.length > 0 && <>
      <div style={{ ...label, marginTop:14, display:"flex", alignItems:"center", gap:12 }}>
        CSC history
        <label style={{ textTransform:"none", letterSpacing:0, fontWeight:400, fontSize:12, cursor:"pointer" }}>
          <input type="checkbox" checked={showInternal} onChange={e=>setShowInternal(e.target.checked)}
                 style={{ marginRight:4 }} />include CSC-internal notes</label>
      </div>
      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12.5 }}>
        <tbody>{history.map((n, i) => (
          <tr key={i}>
            <Td style={{ width:130, whiteSpace:"nowrap" }}>{fmtWhen(n.at)}</Td>
            {orders.length > 1 && <Td style={{ width:100 }}>{n.ev}</Td>}
            <Td style={{ width:170, fontWeight:500 }}>{n.type}</Td>
            <Td style={{ whiteSpace:"normal" }}>{n.comment}</Td>
            <Td style={{ width:150, color:"var(--color-text-secondary)" }}>{n.by || ""}</Td>
          </tr>))}</tbody>
      </table>
    </>}
  </div>;
};

// ── Correcting who a filing belongs to ──────────────────────────────────────
// For a filing confirmed against the wrong borrower (a CSC review pick, or a
// hand entry), and for the filed date of an original recorded from a later
// change. Changes only these fields; the filing keeps its history and its CSC
// link, and the correction is written into its notes. Nothing is deleted.
const CorrectFiling = ({ v, onCancel, onSaved }) => {
  const { api } = useApp();
  const [accounts, setAccounts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [acct, setAcct] = useState(v.accountid ? String(v.accountid) : "");
  const [ent, setEnt] = useState(v.borrowerguarantorid ? String(v.borrowerguarantorid) : "");
  const [fd, setFd] = useState(v.filedate ? String(v.filedate).slice(0, 10) : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("getAccounts").then(d=>setAccounts(Array.isArray(d)?d:[])).catch(()=>{});
    api.get("getBorrowersGuarantors").then(d=>setEntities(Array.isArray(d)?d:[])).catch(()=>{});
  }, []);
  const mine = entities.filter(e => String(e.accountid) === String(acct));
  const inp = { width:"100%", boxSizing:"border-box", padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #999" };
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post("correctUCCFiling", { id: v.uccfilingid, accountid: acct,
                                                     borrowerguarantorid: ent || null, filedate: fd || null });
      onSaved(r && r.changed && r.changed.length ? "Corrected: " + r.changed.join("; ") : "Nothing changed");
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  return <>
    <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>Correct borrower / filed date</div>
    <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:10 }}>
      Moves this filing — with its history and CSC link — to the right borrower. The change is noted on the filing.
    </div>
    <div style={{ display:"grid", gridTemplateColumns:"2fr 2fr 1fr", gap:"0 16px" }}>
      <Field label="Borrower">
        <select value={acct} style={inp} onChange={e=>{ setAcct(e.target.value); setEnt(""); }}>
          <option value="">— Select —</option>
          {accounts.map(a=><option key={a.id} value={a.id}>{a.companyname}</option>)}
        </select>
      </Field>
      <Field label="Debtor entity (optional)">
        <select value={ent} style={inp} disabled={!acct} onChange={e=>setEnt(e.target.value)}>
          <option value="">—</option>
          {mine.map(e=><option key={e.id} value={e.id}>{e.borrowerguarantorname}</option>)}
        </select>
      </Field>
      <Field label="Date filed">
        <input type="date" value={fd} onChange={e=>setFd(e.target.value)} style={inp} />
      </Field>
    </div>
    {err && <div style={{ color:"#993C1D", fontSize:12, marginTop:8 }}>{err}</div>}
    <div style={{ display:"flex", gap:8, marginTop:12 }}>
      <Btn variant="primary" disabled={busy || !acct} onClick={save}>{busy ? "Saving…" : "Save correction"}</Btn>
      <Btn disabled={busy} onClick={onCancel}>Cancel</Btn>
    </div>
  </>;
};

// ── Recording a filing that already exists at a filing office ────────────────
// Exported: opened from an account's Monitoring tab, where the borrower is
// that account (accountid set, and not changeable here).
// With rec: editing a filing made outside CSC (every field, same form).
export const FilingForm = ({ onClose, onSaved, accountid, rec }) => {
  const { api } = useApp();
  const [accounts, setAccounts] = useState([]);
  const [entities, setEntities] = useState([]);
  const s = v => (v == null ? "" : String(v));
  const [f, setF] = useState(rec ? {
    accountid: s(rec.accountid), borrowerguarantorid: s(rec.borrowerguarantorid),
    jurisdiction_state: s(rec.jurisdiction_state), jurisdiction_office: s(rec.jurisdiction_office),
    filingnumber: s(rec.filingnumber), filedate: rec.filedate ? String(rec.filedate).slice(0, 10) : "",
    lapse_years: rec.lapse_years == null ? "" : String(rec.lapse_years),
    debtorname_asfiled: s(rec.debtorname_asfiled), debtorstate_asfiled: s(rec.debtorstate_asfiled),
    securedparty_asfiled: s(rec.securedparty_asfiled), collateraldescription: s(rec.collateraldescription),
    box_url: s(rec.box_url), source: s(rec.source) || "counsel", notes: s(rec.notes),
    // carried through untouched so a save does not blank them
    debtoraddress_asfiled: s(rec.debtoraddress_asfiled), box_file_id: s(rec.box_file_id),
    csc_jurisdiction_source: s(rec.csc_jurisdiction_source), csc_orderid: s(rec.csc_orderid), csc_filingid: s(rec.csc_filingid),
  } : {
    accountid: accountid ? String(accountid) : "", borrowerguarantorid:"", jurisdiction_state:"", jurisdiction_office:"",
    filingnumber:"", filedate:"", lapse_years:"5", debtorname_asfiled:"", debtorstate_asfiled:"",
    securedparty_asfiled:"", collateraldescription:"", box_url:"", source:"counsel", notes:"",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = k => val => setF(x => ({ ...x, [k]: val }));

  useEffect(() => {
    api.get("getAccounts").then(d=>setAccounts(Array.isArray(d)?d:[])).catch(()=>{});
    api.get("getBorrowersGuarantors").then(d=>setEntities(Array.isArray(d)?d:[])).catch(()=>{});
  }, []);

  const mine = entities.filter(e => String(e.accountid) === String(f.accountid));

  // Picking the entity PREFILLS the as-filed block from what we know today.
  // Prefill, not binding: the fields stay editable because the register records
  // what the filing says, and an older filing may well name a former name.
  const pickEntity = id => {
    const e = entities.find(x => String(x.id) === String(id));
    setF(x => ({ ...x,
      borrowerguarantorid: id,
      debtorname_asfiled: x.debtorname_asfiled || (e ? e.borrowerguarantorname || "" : ""),
      debtorstate_asfiled: x.debtorstate_asfiled || (e ? e.stateoforganization || "" : ""),
      jurisdiction_state: x.jurisdiction_state || (e ? e.stateoforganization || "" : ""),
    }));
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const body = { ...f, lapse_years: f.lapse_years === "" ? null : f.lapse_years,
                     ...(rec ? { id: rec.uccfilingid } : {}) };
      await api.post("saveUCCFiling", body);
      onSaved(rec ? "Filing updated" : "Filing recorded");
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const inp = { width:"100%", boxSizing:"border-box", padding:"7px 10px", fontSize:13,
                borderRadius:4, border:"1px solid #999" };

  return <Modal title={rec ? "Edit Filing Made Outside CSC" : "Add a Filing Made Outside CSC"} onClose={onClose} wide>
    <div style={{ padding:20 }}>
      <div style={{ fontSize:12.5, color:"#7a5200", background:"#FAEEDA", border:"0.5px solid #EBD3A0",
                    borderRadius:8, padding:"10px 14px", marginBottom:14 }}>
        <strong>Only for a filing another firm made directly with a filing office, not through CSC.</strong>{" "}
        This records a financing statement that already exists — it does not file anything, and nothing is
        sent to CSC. Anything ordered through CSC comes in with <em>Pull from CSC</em> instead. Put the stamped
        copy from the other firm in Box and paste its link below.
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 16px" }}>
        <div style={{ gridColumn:"1/3" }}>
          <Field label="Borrower">
            <select value={f.accountid} style={inp} disabled={!!accountid}
                    onChange={e=>setF(x=>({ ...x, accountid:e.target.value, borrowerguarantorid:"" }))}>
              <option value="">— Select —</option>
              {accounts.map(a=><option key={a.id} value={a.id}>{a.companyname}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ gridColumn:"3/-1" }}>
          <Field label="Debtor entity">
            <select value={f.borrowerguarantorid} disabled={!f.accountid} style={inp}
                    onChange={e=>pickEntity(e.target.value)}>
              <option value="">{f.accountid ? "— Select —" : "— Pick a borrower first —"}</option>
              {mine.map(e=><option key={e.id} value={e.id}>
                {e.borrowerguarantorname}{e.borrowerguarantor ? " · " + e.borrowerguarantor : ""}
              </option>)}
            </select>
          </Field>
        </div>

        <div style={{ gridColumn:"1/3" }}>
          <Field label="Debtor name AS FILED"><FInput value={f.debtorname_asfiled} onChange={set("debtorname_asfiled")} /></Field>
        </div>
        <Field label="Debtor state as filed">
          <select value={f.debtorstate_asfiled} onChange={e=>set("debtorstate_asfiled")(e.target.value)} style={inp}>
            <option value="">—</option>
            {STATE_CODES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Filing office state">
          <select value={f.jurisdiction_state} onChange={e=>set("jurisdiction_state")(e.target.value)} style={inp}>
            <option value="">—</option>
            {STATE_CODES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        </Field>

        <Field label="Filing number"><FInput value={f.filingnumber} onChange={set("filingnumber")} /></Field>
        <Field label="Date filed">
          <input type="date" value={f.filedate} onChange={e=>set("filedate")(e.target.value)} style={inp} />
        </Field>
        {/* 5 under 9-515(a); 30 for a manufactured-home or public-finance
            transaction; blank for a transmitting utility, which never lapses. */}
        <Field label="Lapses after (years)">
          <select value={f.lapse_years} onChange={e=>set("lapse_years")(e.target.value)} style={inp}>
            <option value="5">5 — ordinary</option>
            <option value="30">30 — manufactured home / public finance</option>
            <option value="">never — transmitting utility</option>
          </select>
        </Field>
        <Field label="Learned from">
          <select value={f.source} onChange={e=>set("source")(e.target.value)} style={inp}>
            <option value="counsel">Outside counsel (filed directly)</option>
            <option value="internal">Filed in-house</option>
            <option value="legacy">Legacy record</option>
          </select>
        </Field>

        <div style={{ gridColumn:"1/3" }}>
          <Field label="Filing office"><FInput value={f.jurisdiction_office} onChange={set("jurisdiction_office")} /></Field>
        </div>
        <div style={{ gridColumn:"3/-1" }}>
          <Field label="Secured party as filed"><FInput value={f.securedparty_asfiled} onChange={set("securedparty_asfiled")} /></Field>
        </div>
        <div style={{ gridColumn:"1/-1" }}>
          <Field label="Collateral description"><FInput value={f.collateraldescription} onChange={set("collateraldescription")} /></Field>
        </div>
        <div style={{ gridColumn:"1/-1" }}>
          <Field label="Box link to the stamped copy"><FInput value={f.box_url} onChange={set("box_url")} /></Field>
        </div>
      </div>

      {err && <div style={{ color:"#993C1D", fontSize:12, marginTop:8 }}>{err}</div>}
      <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" disabled={busy || !f.accountid} onClick={save}>
          {busy ? "Saving…" : rec ? "Save changes" : "Record Filing"}
        </Btn>
      </div>
    </div>
  </Modal>;
};

// ── CSC: pull order history, then confirm what needs a person ────────────────
const parseDocs = d => { try { const a = typeof d === "string" ? JSON.parse(d) : d; return Array.isArray(a) ? a : []; }
                         catch (e) { return []; } };
const fmtStamp = t => t ? new Date(t).toLocaleString() : "—";

const CSCPullModal = ({ summary, onClose, onDone }) => {
  const { api } = useApp();
  const [busy, setBusy] = useState(null);       // "preview" | "pull" | null
  const [preview, setPreview] = useState(null);
  const [prog, setProg] = useState(null);
  const [err, setErr] = useState(null);
  const [stop, setStop] = useState(false);
  const stopRef = useRef(false);
  const [probeId, setProbeId] = useState("");
  const [probe, setProbe] = useState(null);

  const runProbe = async () => {
    setBusy("probe"); setErr(null); setProbe(null);
    try { setProbe(await api.post("runCSCSync", { probeOrder: probeId.trim() })); }
    catch (e) { setErr(e.message); }
    setBusy(null);
  };
  const last = summary && summary.lastRun;

  const runPreview = async () => {
    setBusy("preview"); setErr(null); setPreview(null);
    try { setPreview(await api.post("runCSCSync", { dryRun: true })); }
    catch (e) { setErr(e.message); }
    setBusy(null);
  };

  // Each call works ~30 seconds and says whether there is more; keep calling
  // until the history is caught up. Safe to stop — the next pull resumes.
  const runPull = async () => {
    setBusy("pull"); setErr(null); setPreview(null); setStop(false); stopRef.current = false;
    const t = { stored:0, linked:0, forReview:0, pending:0, searches:0, failed:0, fetched:0, unchanged:0, windowEnd:null, errors:[] };
    let more = true, n = 0;
    try {
      while (more && n < 200) {
        n++;
        const r = await api.post("runCSCSync", {});
        ["stored","linked","forReview","pending","searches","failed","fetched","unchanged"]
          .forEach(k => { t[k] += r[k] || 0; });
        t.windowEnd = r.windowEnd || t.windowEnd;
        if (Array.isArray(r.errors)) t.errors = t.errors.concat(r.errors).slice(0, 10);
        setProg({ ...t, calls:n, more:!!r.more });
        more = !!r.more;
        if (r.failed && !r.more) break;
        if (stopRef.current) break;
      }
      setBusy(null);
      onDone("CSC pull: " + t.fetched + " order(s) read, " + t.linked + " attached to the register, "
             + t.forReview + " waiting for review" + (t.failed ? ", " + t.failed + " failed" : "") + ".");
    } catch (e) { setErr(e.message); setBusy(null); }
  };

  const th = { textAlign:"left", fontSize:11, color:"var(--color-text-secondary)", padding:"4px 8px",
               borderBottom:"0.5px solid var(--color-border-tertiary)" };
  const td = { fontSize:12, padding:"4px 8px", borderBottom:"0.5px solid var(--color-border-tertiary)", verticalAlign:"top" };

  return <Modal title="Pull from CSC" onClose={busy ? ()=>{} : onClose} wide>
    <div style={{ padding:20, fontSize:13 }}>
      <div style={{ color:"var(--color-text-secondary)", marginBottom:12 }}>
        Reads the CSC order history — every UCC filing, continuation, amendment and termination, whoever
        placed it — and brings it into the register. Changes to filings already here attach automatically; new
        filings wait below for you to confirm the borrower. This only reads from CSC. It never orders anything.
      </div>

      <div style={{ background:"var(--color-background-secondary)", borderRadius:8, padding:"10px 14px", marginBottom:14 }}>
        {summary == null
          ? <span>CSC history isn't set up yet — run <code>sql/2026-10-14_csc_order_sync.sql</code>, then come back.</span>
          : <>
              <div><strong>Pulled through:</strong> {summary.pulledThrough ? fmtStamp(summary.pulledThrough) : "never — the first pull reads everything since 2020 and may take a few minutes"}</div>
              {last && <div style={{ marginTop:4, color:"var(--color-text-secondary)" }}>
                Last run {fmtStamp(last.startedat)} by {last.ranby || "—"} · {last.status}
                {last.environment ? " · " + last.environment : ""}{last.error ? " · " + last.error : ""}
              </div>}
              {summary.cscEnabled === false && <div style={{ marginTop:4, color:"#993C1D" }}>
                The CSC integration is switched off on the Integrations page.</div>}
            </>}
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:14 }}>
        <Btn disabled={!!busy || summary == null} onClick={runPreview}>{busy==="preview" ? "Reading CSC…" : "Preview (saves nothing)"}</Btn>
        <Btn variant="primary" disabled={!!busy || summary == null} onClick={runPull}>{busy==="pull" ? "Pulling…" : "Pull now"}</Btn>
        {busy==="pull" && <Btn onClick={()=>{ stopRef.current = true; setStop(true); }} disabled={stop}>
          {stop ? "Stopping after this batch…" : "Stop"}</Btn>}
        {!busy && <Btn onClick={onClose}>Close</Btn>}
      </div>

      {err && <div style={{ color:"#993C1D", fontSize:12.5, marginBottom:10 }}>{err}</div>}

      {/* Nothing coming back? Check one order you know exists in CSC — it tells
          "the API user cannot see our orders" apart from "the feed is empty". */}
      <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:12, fontSize:12.5 }}>
        <span style={{ color:"var(--color-text-secondary)" }}>Check one CSC order #</span>
        <input value={probeId} onChange={e=>setProbeId(e.target.value)} placeholder="e.g. 3120717"
               style={{ padding:"5px 8px", fontSize:12.5, borderRadius:4, border:"1px solid #999", width:130 }} />
        <Btn disabled={!!busy || !probeId.trim() || summary == null} onClick={runProbe}>
          {busy==="probe" ? "Checking…" : "Check"}</Btn>
      </div>
      {probe && <div style={{ fontSize:12.5, marginBottom:12,
                              color: probe.found ? "inherit" : "#993C1D" }}>
        {probe.found
          ? <>Order {probe.probeOrder} found ({probe.environment}) · {probe.orderStatus || "—"} · {probe.documents} document(s)
              {(probe.rows || []).map((r,i) => <div key={i} style={{ color:"var(--color-text-secondary)" }}>
                {r.kind === "filing" ? r.eventtype : r.kind} · {r.filingnumber || "no filing #"} · {r.state || "—"} · {fmtDate(r.filingdate)} · {r.debtor || "—"}{r.cscuser ? " · placed by " + r.cscuser : ""}
              </div>)}</>
          : <>CSC could not return order {probe.probeOrder} ({probe.environment}): {probe.error}</>}
        {probe.raw && <details style={{ marginTop:6 }}>
          <summary style={{ cursor:"pointer", color:B.cyan }}>What CSC sent ({(probe.keys || []).join(", ")})</summary>
          <Btn onClick={()=>{ try { navigator.clipboard.writeText(probe.raw); } catch (e) {} }}>Copy</Btn>
          <pre style={{ maxHeight:260, overflow:"auto", fontSize:11, background:"var(--color-background-secondary)",
                        padding:8, borderRadius:6, whiteSpace:"pre-wrap" }}>{probe.raw}</pre>
        </details>}
      </div>}

      {prog && <div style={{ fontSize:12.5, marginBottom:10 }}>
        {busy==="pull" && <span style={{ fontWeight:600 }}>Working… </span>}
        Read through <strong>{prog.windowEnd ? fmtDate(prog.windowEnd) : "—"}</strong> ·
        {" "}{prog.fetched} orders read · {prog.unchanged} unchanged · {prog.linked} attached ·
        {" "}{prog.forReview} for review · {prog.pending} not filed yet · {prog.searches} searches
        {prog.failed ? <span style={{ color:"#993C1D" }}> · {prog.failed} failed</span> : null}
        {prog.errors.length > 0 && <div style={{ color:"#993C1D", marginTop:4 }}>
          {prog.errors.map((e,i)=><div key={i}>{typeof e === "string" ? e : (e.id ? e.id + ": " : "") + (e.error || JSON.stringify(e))}</div>)}
        </div>}
      </div>}

      {preview && <div>
        {Array.isArray(preview.probe) && <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:6 }}>
          CSC answered ({preview.environment}): {preview.probe.map(p =>
            p.status + " " + (p.count == null ? "error" : p.count)).join(" · ")}
          {preview.probe.filter(p => p.error).map((p,i) =>
            <div key={i} style={{ color:"#993C1D" }}>{p.status}: {p.error}</div>)}
        </div>}
        <div style={{ fontSize:12.5, marginBottom:8 }}>
          {preview.orderLines || 0} order line(s) between {fmtDate(preview.windowStart)} and {fmtDate(preview.windowEnd)}.
          {" "}Showing the first {(preview.preview || []).length}. Nothing was saved.
          {preview.failed ? <span style={{ color:"#993C1D" }}> {preview.failed} could not be read: {(preview.errors||[]).map(e=>typeof e==="string"?e:(e.error||"")).join("; ")}</span> : null}
        </div>
        {(preview.preview || []).length > 0 && <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr>{["Order","Type","Filing #","Filed","State","Debtor","Secured party","Docs"].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{preview.preview.map(p => <tr key={p.csc_orderid}>
              <td style={td}>{p.csc_orderid}<div style={{ color:"var(--color-text-secondary)" }}>{p.status}</div></td>
              <td style={td}>{p.kind === "filing" ? p.eventtype : p.kind}<div style={{ color:"var(--color-text-secondary)" }}>{p.filingtype}</div></td>
              <td style={td}>{p.filingnumber || "—"}{p.initialfilenumber ? <div style={{ color:"var(--color-text-secondary)" }}>re {p.initialfilenumber}</div> : null}</td>
              <td style={td}>{fmtDate(p.filingdate)}</td>
              <td style={td}>{p.state || "—"}</td>
              <td style={{ ...td, whiteSpace:"normal", maxWidth:220 }}>{p.debtor || "—"}</td>
              <td style={{ ...td, whiteSpace:"normal", maxWidth:180 }}>{p.securedparty || "—"}</td>
              <td style={td}>{p.documents}</td>
            </tr>)}</tbody>
          </table>
        </div>}
      </div>}
    </div>
  </Modal>;
};

const CSCQueue = ({ summary, filings, canEdit, onChanged }) => {
  const { api, go } = useApp();
  const [view, setView] = useState("review");
  const [rows, setRows] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!summary) return;
    if (view === "review") { setRows(summary.rows || []); return; }
    setRows(null);
    api.get("getCSCOrders", { view }).then(d => setRows((d && d.rows) || [])).catch(() => setRows([]));
  }, [summary, view]);

  useEffect(() => {
    if (!canEdit || !summary) return;
    api.get("getAccounts").then(d=>setAccounts(Array.isArray(d)?d:[])).catch(()=>{});
    api.get("getBorrowersGuarantors").then(d=>setEntities(Array.isArray(d)?d:[])).catch(()=>{});
  }, [canEdit, !!summary]);

  if (!summary) return null;
  const c = summary.counts || {};
  if (!(c.review || c.pending || c.ignored)) return null;

  const tab = (k, label, n) => (
    <span onClick={()=>setView(k)} style={{ cursor:"pointer", fontSize:12.5, padding:"3px 10px", borderRadius:4,
          fontWeight: view===k ? 600 : 400,
          background: view===k ? "var(--color-background-secondary)" : "transparent" }}>{label} ({n || 0})</span>
  );

  return <Card style={{ marginBottom:16, padding:0 }}>
    <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", flexWrap:"wrap",
                  borderBottom: open ? "0.5px solid var(--color-border-tertiary)" : "none" }}>
      <strong style={{ fontSize:13.5, cursor:"pointer" }} onClick={()=>setOpen(o=>!o)}>
        {open ? "▾" : "▸"} From CSC — needs review
      </strong>
      {open && <>{tab("review","Needs review",c.review)}{tab("pending","Not filed yet",c.pending)}{tab("ignored","Ignored",c.ignored)}</>}
      <span style={{ marginLeft:"auto", fontSize:11.5, color:"var(--color-text-secondary)" }}>
        {c.linked || 0} in register
        {/* Searches have their own page: tie-out, report reading, findings. */}
        {c.searches ? <> · <a href="#" onClick={e=>{ e.preventDefault(); go("ucc-searches"); }}
                              style={{ color:B.cyan }}>{c.searches} searches → UCC Searches</a></> : null}
      </span>
    </div>
    {open && <div style={{ padding:"4px 14px 10px" }}>
      {view === "review" && <div style={{ fontSize:12, color:"var(--color-text-secondary)", margin:"8px 0" }}>
        New filings need a borrower before they join the register. Changes (continuations, amendments,
        terminations) whose original filing isn't in the register yet sit here too — add the original first
        and they attach on their own.
      </div>}
      {rows == null ? <Spinner /> : rows.length === 0
        ? <div style={{ fontSize:12.5, color:"var(--color-text-secondary)", padding:"10px 0" }}>Nothing here.</div>
        : CSC_GROUPS.map(g => {
            const list = rows.filter(o => cscGroupOf(o) === g.key);
            return list.length ? <CSCGroup key={g.key} label={g.label} hint={g.hint} count={list.length}>
              {list.map(o => <CSCRow key={o.csc_orderid} o={o} view={view} canEdit={canEdit}
                                     accounts={accounts} entities={entities} filings={filings} onChanged={onChanged} />)}
            </CSCGroup> : null;
          })}
    </div>}
  </Card>;
};

// The queue in the order it should be worked: a UCC-1 has to be in the
// register before anything that changes it can attach, so Initials lead and
// Terminations — which only close out a filing — come last.
const CSC_GROUPS = [
  { key:"Initial",      label:"Initial filings (UCC-1)", hint:"Pick the borrower and add to the register. Changes below attach on their own once their original is in." },
  { key:"Continuation", label:"Continuations",           hint:"Extend a filing's lapse date. Attach to the original filing." },
  { key:"Amendment",    label:"Amendments",              hint:"Change parties or collateral on a filing. Attach to the original filing." },
  { key:"Assignment",   label:"Assignments",             hint:"Move the secured party's interest. Attach to the original filing." },
  { key:"Termination",  label:"Terminations",            hint:"End a filing. Attach to the original filing; it will show as Terminated." },
  { key:"Other",        label:"Other",                   hint:"" },
];
const cscGroupOf = o => (CSC_GROUPS.some(g => g.key === o.eventtype) ? o.eventtype : "Other");

const CSCGroup = ({ label, hint, count, children }) => {
  const [open, setOpen] = useState(true);
  return <div style={{ marginTop:10 }}>
    <div onClick={()=>setOpen(x=>!x)}
         style={{ cursor:"pointer", display:"flex", alignItems:"baseline", gap:10, padding:"8px 12px",
                  background:B.cyanLight, borderRadius:4 }}>
      <strong style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em",
                       color:B.cyanDark }}>{open ? "▾" : "▸"} {label} ({count})</strong>
      {hint && <span style={{ fontSize:11.5, color:B.cyanDark, opacity:0.8 }}>{hint}</span>}
    </div>
    {open && <div style={{ padding:"0 4px" }}>{children}</div>}
  </div>;
};

// CSC searches: see UCCSearchesPage.js (their own page).

const CSCRow = ({ o, view, canEdit, accounts, entities, filings, onChanged }) => {
  const { api } = useApp();
  const isInitial = o.eventtype === "Initial";
  const [acct, setAcct] = useState(o.suggested_accountid ? String(o.suggested_accountid) : "");
  const [ent, setEnt] = useState(o.suggested_entityid ? String(o.suggested_entityid) : "");
  // An Initial with no CSC expiration is almost always a transmitting utility,
  // so its default is "never"; a recorded original defaults to 5 years.
  const [lapse, setLapse] = useState(isInitial && !o.expirationdate ? "" : "5");
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const docs = parseDocs(o.documents);
  const mine = entities.filter(e => String(e.accountid) === String(acct));

  // Candidate filings for a change: same filing office state first.
  const candidates = [...filings].sort((a, b) =>
    (b.jurisdiction_state === o.jurisdiction_state) - (a.jurisdiction_state === o.jurisdiction_state)
    || String(a.companyname || "").localeCompare(String(b.companyname || "")));

  const act = async (action, extra) => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post("saveCSCOrderReview", { csc_orderid: o.csc_orderid, action, ...extra });
      const msg = action === "add" && r && r.existing
        ? "CSC filing " + (o.filingnumber || o.csc_orderid) + " was already in the register (entered by hand) — linked to it."
        : action === "add"
        ? "Added CSC filing " + (o.filingnumber || o.csc_orderid) + " to the register"
          + (r && r.attached ? " — " + r.attached + " later change(s) attached with it" : "") + "."
        : action === "addOriginal" ? "Recorded original filing " + o.initialfilenumber + " and attached the "
          + String(o.eventtype).toLowerCase() + " to it" + (r && r.attached ? " (plus " + r.attached + " more)" : "") + "."
        : action === "attach" ? "Attached the " + String(o.eventtype).toLowerCase() + " to its filing."
        : action === "ignore" ? "CSC order " + o.csc_orderid + " ignored."
        : "CSC order " + o.csc_orderid + " back in review.";
      onChanged(msg);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const sel = { padding:"5px 8px", fontSize:12.5, borderRadius:4, border:"1px solid #999", maxWidth:260 };
  // A change whose original is not in the register (and cannot be, from a
  // CSC UCC-1) starts on "add the original from this"; otherwise on attach.
  const [mode, setMode] = useState(() => (!isInitial && o.initialfilenumber && !filings.some(f =>
    String(f.filingnumber || "").toUpperCase().replace(/[^A-Z0-9]/g, "")
      === String(o.initialfilenumber).toUpperCase().replace(/[^A-Z0-9]/g, ""))) ? "original" : "attach");
  const entityPick = <>
    <select value={acct} style={sel} onChange={e=>{ setAcct(e.target.value); setEnt(""); }}>
      <option value="">— Borrower —</option>
      {accounts.map(a=><option key={a.id} value={a.id}>{a.companyname}</option>)}
    </select>
    <select value={ent} style={sel} disabled={!acct} onChange={e=>setEnt(e.target.value)}>
      <option value="">— Debtor entity (optional) —</option>
      {mine.map(e=><option key={e.id} value={e.id}>{e.borrowerguarantorname}</option>)}
    </select>
  </>;
  const lapsePick =
    <select value={lapse} style={{ ...sel, width:120 }} onChange={e=>setLapse(e.target.value)}>
      <option value="5">Lapses 5 yrs</option>
      <option value="30">30 yrs (mfd. home)</option>
      <option value="">Never (transmitting utility)</option>
    </select>;
  const suggestedNote = o.suggested_company && String(acct) === String(o.suggested_accountid) &&
    <span style={{ fontSize:11.5, color:"var(--color-text-secondary)" }}>suggested from the debtor name</span>;
  const muted = { color:"var(--color-text-secondary)" };

  return <div style={{ borderTop:"0.5px solid var(--color-border-tertiary)", padding:"10px 0", fontSize:12.5 }}>
    <div style={{ display:"flex", gap:16, flexWrap:"wrap", alignItems:"baseline" }}>
      <span style={{ fontWeight:600 }}>{o.eventtype || o.kind}</span>
      <span style={{ fontFamily:"var(--font-mono)" }}>{o.filingnumber || "no filing # yet"}</span>
      <span>{o.jurisdiction_state || "—"}{o.jurisdiction_office ? " · " + o.jurisdiction_office : ""}</span>
      <span>Filed {fmtDate(o.filingdate)}</span>
      {o.expirationdate && <span style={muted}>Lapses {fmtDate(o.expirationdate)}</span>}
      {o.initialfilenumber && <span style={muted}>Changes {o.initialfilenumber}</span>}
      <span style={muted}>CSC {o.csc_orderid} · {o.cscstatus}{o.cscuser ? " · " + o.cscuser : ""}</span>
    </div>
    <div style={{ marginTop:4 }}><span style={muted}>Debtor:</span> {o.debtornames || "—"}
      {o.debtorstate ? <span style={muted}> ({o.debtorstate})</span> : null}</div>
    {o.securedparty && <div style={{ marginTop:2 }}><span style={muted}>Secured party:</span> {o.securedparty}</div>}
    {docs.length > 0 && <div style={{ marginTop:4, display:"flex", gap:12, flexWrap:"wrap" }}>
      {docs.map(d => <a key={d.id} href="#" onClick={e=>{ e.preventDefault(); openCscDoc(api, o.csc_orderid, d.id); }}
                       style={{ color:B.cyan }}>📄 {d.description || "Document"}</a>)}
    </div>}

    {canEdit && view === "review" && <div style={{ display:"flex", gap:8, marginTop:8, flexWrap:"wrap", alignItems:"center" }}>
      {isInitial ? <>
        {entityPick}
        {/* CSC reports the expiration date; the register takes its lapse from
            that. Only a filing with none (a transmitting utility) asks. */}
        {o.expirationdate
          ? <span style={{ fontSize:12, ...muted }}>Lapses {fmtDate(o.expirationdate)} (from CSC)</span>
          : lapsePick}
        <Btn variant="primary" disabled={busy || !acct || !o.filingnumber}
             onClick={()=>act("add", { accountid: acct, borrowerguarantorid: ent || null,
                                       lapse_years: o.expirationdate ? undefined : (lapse === "" ? null : lapse) })}>
          {busy ? "Adding…" : "Add to register"}</Btn>
        {suggestedNote}
      </> : mode === "original" ? <>
        <span style={{ fontSize:12, ...muted }}>Original {o.initialfilenumber}:</span>
        {/* No lapse choice here. A termination ends the filing, so its lapse is
            moot; a continuation carries the new lapse date itself; anything
            else takes the standard five years, editable on the filing once
            its filed date is entered. */}
        {entityPick}
        <Btn variant="primary" disabled={busy || !acct}
             onClick={()=>act("addOriginal", { accountid: acct, borrowerguarantorid: ent || null })}>
          {busy ? "Adding…" : "Add original & attach"}</Btn>
        {suggestedNote}
        {candidates.length > 0 &&
          <a href="#" onClick={e=>{ e.preventDefault(); setMode("attach"); }} style={{ fontSize:12, color:B.cyan }}>
            or attach to a filing already in the register</a>}
      </> : <>
        <select value={target} style={{ ...sel, maxWidth:380 }} onChange={e=>setTarget(e.target.value)}>
          <option value="">— Filing this changes —</option>
          {candidates.map(f=><option key={f.uccfilingid} value={f.uccfilingid}>
            {f.companyname} · {f.jurisdiction_state} {f.filingnumber}</option>)}
        </select>
        <Btn variant="primary" disabled={busy || !target}
             onClick={()=>act("attach", { uccfilingid: target })}>{busy ? "Attaching…" : "Attach"}</Btn>
        {o.initialfilenumber &&
          <a href="#" onClick={e=>{ e.preventDefault(); setMode("original"); }} style={{ fontSize:12, color:B.cyan }}>
            original not in the register? Add it from this</a>}
      </>}
      <Btn disabled={busy} onClick={()=>act("ignore")}>Ignore</Btn>
    </div>}
    {canEdit && view === "ignored" && <div style={{ marginTop:8 }}>
      <Btn disabled={busy} onClick={()=>act("restore")}>Back to review</Btn></div>}
    {err && <div style={{ color:"#993C1D", marginTop:6 }}>{err}</div>}
  </div>;
};
